import { Checkbox, Grid, Group, TagsInput, Text, TextInput } from '@mantine/core';
import { codes as countriesAndCodes } from 'country-calling-code';
import { getCode } from 'country-list';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useFetch from '../../helpers/useFetch';
import useIsSuperAdmin from '../../helpers/useIsSuperAdmin';
import ErrorMessage from '../ErrorMessage';
import SearchableCombobox from '../SearchableCombobox';
import SuperAdminBadge from '../SuperAdmin/SuperAdminBadge';

const AccountBillingFields = ({ billingInfo, setBillingInfo, error }) => {
  const [partners, setPartners] = useState([]);
  const [loadError, setLoadError] = useState();
  const fetch = useFetch();

  const handleChange = (e) => {
    setBillingInfo((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const isSuperAdmin = useIsSuperAdmin();

  useEffect(() => {
    if (isSuperAdmin) {
      fetch(`/api/admin/partners`)
        .then(({ partners }) => {
          setPartners(partners);
        })
        .catch(setLoadError);
    }
  }, [fetch, isSuperAdmin]);

  const countryOptions = useMemo(() => countriesAndCodes.map((c) => ({ label: c.country, value: c.country })), []);

  const currencyOptions = useMemo(
    () => [
      { label: 'US Dollar (USD)', value: 'USD' },
      { label: 'Bulgarian Lev (BGN)', value: 'BGN' },
      { label: 'Euro (EUR)', value: 'EUR' },
    ],
    []
  );

  const partnerOptions = useMemo(() => partners.map((p) => ({ label: p.name, value: p.id.toString() })), [partners]);

  const handleCountryChange = useCallback(
    (value) => setBillingInfo((prev) => ({ ...prev, country: value })),
    [setBillingInfo]
  );

  const handleCurrencyChange = useCallback((value) => handleChange({ target: { name: 'invoiceCurrency', value } }), []);

  const handlePartnerChange = useCallback((value) => handleChange({ target: { name: 'partnerId', value } }), []);

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  return (
    <>
      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Text fw={500}>Legal Entity Name*</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 9 }}>
          <TextInput
            name="companyName"
            required={true}
            error={
              (billingInfo.companyName !== undefined && billingInfo.companyName === '') ||
              (error && error.errors?.find((e) => e.path === 'companyName'))
                ? error?.errors?.find((e) => e.path === 'companyName')?.message || 'company name is required'
                : null
            }
            value={billingInfo.companyName || ''}
            onChange={(e) => handleChange({ target: { name: 'companyName', value: e.target.value } })}
          />
        </Grid.Col>
      </Grid>

      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Text fw={500}>Address Line 1*</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 9 }}>
          <TextInput
            name="addressLine1"
            required={true}
            error={
              (error && error.errors?.find((e) => e.path === 'addressLine1')) ||
              (billingInfo.addressLine1 !== undefined && billingInfo.addressLine1 === '')
                ? error?.errors?.find((e) => e.path === 'addressLine1')?.message || 'address line 1 is required'
                : null
            }
            value={billingInfo.addressLine1 || ''}
            onChange={(e) => handleChange({ target: { name: 'addressLine1', value: e.target.value } })}
          />
        </Grid.Col>
      </Grid>

      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Text fw={500}>Address Line 2</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 9 }}>
          <TextInput
            name="addressLine2"
            error={
              error && error.errors?.find((e) => e.path === 'addressLine2')
                ? error.errors?.find((e) => e.path === 'addressLine2').message || 'address line 2 is invalid'
                : null
            }
            value={billingInfo.addressLine2 || ''}
            onChange={(e) => handleChange({ target: { name: 'addressLine2', value: e.target.value } })}
          />
        </Grid.Col>
      </Grid>

      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Text fw={500}>City*</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <TextInput
            name="city"
            required={true}
            error={
              (billingInfo.city !== undefined && billingInfo.city === '') ||
              (error && error.errors?.find((e) => e.path === 'city'))
                ? error?.errors?.find((e) => e.path === 'city')?.message || 'city is required'
                : null
            }
            value={billingInfo.city || ''}
            onChange={(e) => handleChange({ target: { name: 'city', value: e.target.value } })}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text fw={500}>Post code</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <TextInput
            name="postalCode"
            error={
              error && error.errors?.find((e) => e.path === 'postalCode')
                ? error.errors?.find((e) => e.path === 'postalCode').message || 'postal code is required'
                : null
            }
            value={billingInfo.postalCode || ''}
            onChange={(e) => handleChange({ target: { name: 'postalCode', value: e.target.value } })}
          />
        </Grid.Col>
      </Grid>

      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Text fw={500}>Country*</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 9 }}>
          <SearchableCombobox
            options={countryOptions}
            value={billingInfo.country}
            onChange={handleCountryChange}
            placeholder="Type to search..."
          />
        </Grid.Col>
      </Grid>

      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Text fw={500}>Billing Contact Name*</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 9 }}>
          <TextInput
            name="contactName"
            required={true}
            error={
              (billingInfo.contactName !== undefined && billingInfo.contactName === '') ||
              (error && error.errors?.find((e) => e.path === 'contact'))
                ? error?.errors?.find((e) => e.path === 'contactName')?.message || 'contact name is required'
                : null
            }
            value={billingInfo.contactName || ''}
            onChange={(e) => handleChange({ target: { name: 'contactName', value: e.target.value } })}
          />
        </Grid.Col>
      </Grid>

      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Text fw={500}>Phone Number*</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 9 }}>
          <TextInput
            name="contactPhoneNumber"
            required={true}
            error={
              (billingInfo.contactPhoneNumber !== undefined && billingInfo.contactPhoneNumber === '') ||
              (error && error.errors?.find((e) => e.path === 'contactPhoneNumber'))
                ? error?.errors?.find((e) => e.path === 'contactPhoneNumber')?.message || 'phone number is required'
                : null
            }
            value={billingInfo.contactPhoneNumber || ''}
            onChange={(e) => handleChange({ target: { name: 'contactPhoneNumber', value: e.target.value } })}
          />
        </Grid.Col>
      </Grid>

      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Text fw={500}>VAT Registered</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 9 }}>
          <Checkbox
            name="isVatRegistered"
            checked={billingInfo.isVatRegistered || false}
            onChange={(e) => handleChange({ target: { name: 'isVatRegistered', value: e.target.checked } })}
          />
        </Grid.Col>
      </Grid>

      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Text fw={500}>Tax Number</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 9 }}>
          <TextInput
            name="taxNumber"
            disabled={!billingInfo.country}
            error={
              error && error.errors?.find((e) => e.path === 'taxNumber')
                ? error.errors?.find((e) => e.path === 'taxNumber').message
                : null
            }
            value={billingInfo.taxNumber || ''}
            onChange={(e) => handleChange({ target: { name: 'taxNumber', value: e.target.value } })}
            leftSection={
              billingInfo.isVatRegistered && billingInfo.country ? (
                <Text size="sm">{billingInfo.country === 'Greece' ? 'EL' : getCode(billingInfo.country)}</Text>
              ) : null
            }
            leftSectionWidth={billingInfo.isVatRegistered && billingInfo.country ? 40 : 0}
          />
        </Grid.Col>
      </Grid>

      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Text fw={500}>Invoice Currency*</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 9 }}>
          <SearchableCombobox
            options={currencyOptions}
            onChange={handleCurrencyChange}
            value={billingInfo.invoiceCurrency}
          />
        </Grid.Col>
      </Grid>

      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Text fw={500}>Invoice Recipients*</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 9 }}>
          <TagsInput
            placeholder="email@example.com"
            onChange={(values) => handleChange({ target: { name: 'invoiceRecipients', value: values } })}
            value={billingInfo.invoiceRecipients}
          />
        </Grid.Col>
      </Grid>

      {isSuperAdmin && (
        <Grid align="center" mb="sm">
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <Group gap="xs">
              <Text fw={500}>Partner</Text>
              <SuperAdminBadge show />
            </Group>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 9 }}>
            <SearchableCombobox
              clearable={true}
              options={partnerOptions}
              onChange={handlePartnerChange}
              value={billingInfo.partnerId}
            />
          </Grid.Col>
        </Grid>
      )}
    </>
  );
};

export default AccountBillingFields;
